import {z} from 'zod';

import {RoleFirebaseStringSchema} from './role';

// ============================================================================
// Course Roster Document Schema
// ============================================================================

/**
 * Zod schema for a course roster document stored at
 * `course/{courseId}/rosters/{role}` in Firestore.
 *
 * A roster document is a **sparse map** from Firebase Auth UIDs to `true`.
 * The value `false` is never written by any authorized writer; its presence
 * is unambiguous drift (Invariant I5 tripwire). Parsing will fail if any
 * field value is not exactly `true`.
 *
 * The roster document is a **derived index** of the canonical role documents
 * at `users/{userId}/roles/{courseId}`. The reconciliation job enforces that
 * these two collections remain in sync.
 *
 * This file is the server-side / framework-agnostic definition.
 * The webapp-facing converter lives in
 * `src/apps/webapp/src/course/models/courseRosterDocument.ts` and imports
 * Firestore client-SDK types that are not available here.
 *
 * Shape: `{ [userId: string]: true }`.
 */
export const courseRosterDocumentSchema = z.record(
  z.string().describe('Firebase Auth UID of the enrolled user.'),
  z
    .literal(true)
    .describe(
      'Always `true`. The value `false` is never written; its presence indicates drift.',
    ),
);

/**
 * TypeScript type inferred from {@link courseRosterDocumentSchema}.
 * Represents the data shape of `course/{courseId}/rosters/{role}`.
 * Every key is a userId; every value is the literal `true`.
 */
export type CourseRosterDocument = z.infer<typeof courseRosterDocumentSchema>;

// ============================================================================
// Firestore Path Helpers
// ============================================================================

/** Subcollection name for roster documents within a course. */
export const COURSE_ROSTERS_COLLECTION = 'rosters' as const;

/**
 * Returns the Firestore path for the rosters subcollection of a course.
 *
 * Path: `course/{courseId}/rosters`
 *
 * @param params - Path parameters.
 * @param params.courseId - The course identifier.
 * @return The absolute Firestore collection path string.
 * @throws Never.
 */
export function getCourseRostersCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `course/${courseId}/${COURSE_ROSTERS_COLLECTION}`;
}

/**
 * Returns the Firestore path for a single course roster document.
 *
 * Path: `course/{courseId}/rosters/{role}`
 *
 * The `role` parameter must be one of the 8 canonical role strings. Using a
 * role name outside this set produces a stray roster doc (Drift mode D9).
 *
 * @param params - Path parameters.
 * @param params.courseId - The course identifier.
 * @param params.role - A canonical role string from {@link RoleFirebaseStringSchema}.
 * @return The absolute Firestore document path string.
 * @throws Never.
 */
export function getCourseRosterDocumentPath({
  courseId,
  role,
}: {
  courseId: string;
  role: z.infer<typeof RoleFirebaseStringSchema>;
}): string {
  return `course/${courseId}/${COURSE_ROSTERS_COLLECTION}/${role}`;
}
